import React, { useMemo } from 'react';
import { Animated, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { ChevronRight, Clock3, Target, Trophy, Zap } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WORLD_CUP_WELCOME } from '../../../src/worldcup/worldcup.constants';
import { MatchCard } from '../MatchesView/MatchCard';
import { FuturesCard } from '../PicksView/FuturesCard';
import { SketchFrame } from './SketchFrame';
import { SketchNote } from './SketchNote';
import { WORLD_CUP_WELCOME_RULES_STAGGER_BLOCK_COUNT } from './WorldCupWelcomeScreen.constants';
import {
  WELCOME_RULES_PREVIEW_BOOST_MATCH,
  WELCOME_RULES_PREVIEW_COUNTDOWN_MATCH,
  WELCOME_RULES_PREVIEW_FUTURES,
  WELCOME_RULES_PREVIEW_FUTURES_OPTIONS,
  WELCOME_RULES_PREVIEW_LIVE_MATCH,
  WELCOME_RULES_PREVIEW_SECTIONS,
  type WelcomeRulesPreviewSectionId,
  type WelcomeRulesTitleSegment,
} from './WelcomeRulesPhase.constants';
import {
  useWorldCupWelcomeStaggerEntrance,
  staggerStyle,
} from './useWorldCupWelcomeStaggerEntrance';
import { getWelcomeRulesPhaseStyles } from './WelcomeRulesPhase.styles';

const NO_OP_SAVE_FUTURES = async (): Promise<void> => undefined;

const RULE_SECTION_ICONS: Record<WelcomeRulesPreviewSectionId, typeof Trophy> = {
  futures: Trophy,
  countdown: Clock3,
  boost: Zap,
  live: Target,
};

const NO_OP_TOGGLE_BOOST = (): void => undefined;

export interface WelcomeRulesPhaseProps {
  phase: number;
  isExiting: boolean;
  scrollContentPaddingTop: number;
  onNext: () => void;
  testID?: string;
}

export const WelcomeRulesPhase: React.FC<WelcomeRulesPhaseProps> = ({
  phase,
  isExiting,
  scrollContentPaddingTop,
  onNext,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () => getWelcomeRulesPhaseStyles(theme, { scrollContentPaddingTop }),
    [theme, scrollContentPaddingTop],
  );
  const stagger = useWorldCupWelcomeStaggerEntrance(
    phase,
    1,
    WORLD_CUP_WELCOME_RULES_STAGGER_BLOCK_COUNT,
  );

  const renderRulePreview = (sectionId: WelcomeRulesPreviewSectionId) => {
    if (sectionId === 'futures') {
      return (
        <View pointerEvents="none">
          <FuturesCard
            futures={WELCOME_RULES_PREVIEW_FUTURES}
            futuresLocked={false}
            onSave={NO_OP_SAVE_FUTURES}
            futuresOptions={WELCOME_RULES_PREVIEW_FUTURES_OPTIONS}
          />
        </View>
      );
    }

    if (sectionId === 'countdown') {
      return (
        <View pointerEvents="none">
          <MatchCard layout="tutorial" match={WELCOME_RULES_PREVIEW_COUNTDOWN_MATCH} />
        </View>
      );
    }

    if (sectionId === 'boost') {
      return (
        <View pointerEvents="none">
          <MatchCard
            layout="tutorial"
            match={WELCOME_RULES_PREVIEW_BOOST_MATCH}
            isBoosted
            onToggleBoost={NO_OP_TOGGLE_BOOST}
          />
        </View>
      );
    }

    return (
      <View pointerEvents="none">
        <MatchCard layout="tutorial" match={WELCOME_RULES_PREVIEW_LIVE_MATCH} />
      </View>
    );
  };

  return (
    <View style={styles.phaseRoot}>
      <ScrollView
        style={[styles.scrollPhase, Platform.OS === 'web' && styles.scrollPhaseWeb]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        bounces={Platform.OS !== 'web'}
        testID={testID}
      >
        <Animated.View style={staggerStyle(stagger[0])}>
          <Text style={styles.phaseLabel}>{WORLD_CUP_WELCOME.RULES.LABEL}</Text>
          <Text style={styles.phaseTitle}>{WORLD_CUP_WELCOME.RULES.TITLE}</Text>
          <Text style={styles.phaseSubtitle}>{WORLD_CUP_WELCOME.RULES.SUBTITLE}</Text>
        </Animated.View>

        {WELCOME_RULES_PREVIEW_SECTIONS.map((section, i) => {
          const Icon = RULE_SECTION_ICONS[section.id];
          return (
            <Animated.View key={section.id} style={staggerStyle(stagger[i + 1])}>
              <View style={styles.sectionBlock}>
                <View style={styles.sectionHeaderRow}>
                  <View style={styles.sectionStepBadgeWrap}>
                    <View style={styles.sectionStepBadge}>
                      <Text style={styles.sectionStepBadgeText}>{i + 1}</Text>
                    </View>
                  </View>
                  <View style={styles.sectionTitleRow}>
                    <View style={styles.sectionIconWrap}>
                      <Icon size={16} color={theme.primary} strokeWidth={2.2} />
                    </View>
                    <Text style={styles.sectionTitle}>
                      {section.titleSegments.map(
                        (segment: WelcomeRulesTitleSegment, segmentIndex) => (
                          <Text
                            key={segmentIndex}
                            style={segment.em ? styles.sectionTitleEm : undefined}
                          >
                            {segment.text}
                          </Text>
                        ),
                      )}
                    </Text>
                  </View>
                </View>
                <View style={styles.previewContent}>
                  <SketchFrame radius={section.id === 'futures' ? 16 : 24} seed={i + 1}>
                    {renderRulePreview(section.id)}
                  </SketchFrame>
                </View>
                <SketchNote text={section.note} side={section.noteSide} />
              </View>
            </Animated.View>
          );
        })}
      </ScrollView>

      <Animated.View
        style={[
          styles.footer,
          staggerStyle(stagger[WORLD_CUP_WELCOME_RULES_STAGGER_BLOCK_COUNT - 1]),
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={WORLD_CUP_WELCOME.RULES.CTA}
          disabled={isExiting}
          onPress={onNext}
          style={({ pressed, hovered }) => [
            styles.ctaButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.ctaButtonPressed,
            hovered && Platform.OS === 'web' && styles.ctaButtonHovered,
          ]}
        >
          <Text style={styles.ctaButtonLabel}>{WORLD_CUP_WELCOME.RULES.CTA}</Text>
          <ChevronRight size={16} color={theme.onPrimary} strokeWidth={2.5} />
        </Pressable>
      </Animated.View>
    </View>
  );
};
